import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppRole } from "@/service/AppRoleService";

interface UserInfo {
  address?: { country: string };
  appRoles: AppRole[];
  displayName?: string;
  destinationIndicator?: string;
  employeeID?: string;
  employeeType?: string;
  family_name?: string;
  given_name?: string;
  sub?: string;
  email?: string;
  name?: string;
}

interface UserState {
  userInfo: UserInfo | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: UserState = {
  userInfo: null,
  isLoading: false,
  error: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserInfo: (state, action: PayloadAction<UserInfo>) => {
      state.userInfo = action.payload;
      state.error = null;
    },
    setUserLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUserError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearUser: (state) => {
      state.userInfo = null;
      state.error = null;
      state.isLoading = false;
    },
    updateUserRoles: (state, action: PayloadAction<AppRole[]>) => {
      if (state.userInfo) {
        state.userInfo.appRoles = action.payload;
      }
    },
  },
});

export const {
  setUserInfo,
  setUserLoading,
  setUserError,
  clearUser,
  updateUserRoles,
} = userSlice.actions;

// Selectors
/**
 * Selects the user's email from the Redux store.
 * Returns undefined if user is not authenticated or email is not available.
 */
export const selectUserEmail = (state: {
  user: UserState;
}): string | undefined => state.user.userInfo?.email || state.user.userInfo?.sub;

/**
 * Selects the complete user info from the Redux store.
 */
export const selectUserInfo = (state: { user: UserState }): UserInfo | null =>
  state.user.userInfo;

export default userSlice.reducer;
